/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { CSSProperties, forwardRef, ReactNode, useId, useMemo } from "react";

import Box from "@mui/material/Box";
import MuiRadio from "@mui/material/Radio";
import { Theme } from "@mui/material/styles";

import { DashboardTooltip } from "../DashboardTooltip/DashboardTooltip";
import { DashboardIcon } from "../Icon";
import { DashboardOptionLabel } from "../OptionLabel/DashboardOptionLabel";
import { OptionLabelProps } from "../OptionLabel/types";

import { InputVibe } from "@/common/types/mui";
import { Info } from "@/icons/outlined";

interface RadioProps extends Pick<OptionLabelProps, "alignContent"> {
  title: string;
  size?: "small" | "medium";
  disabled?: boolean;
  vibe?: InputVibe;
  value?: string;
  label?: string | ReactNode;
  checked?: boolean;
  errorMessage?: string;
  row?: boolean;
  fullWidth?: boolean;
  columns?: number;
  className?: string;
  style?: CSSProperties;
  tooltipText?: string;
}

const CIRCLE_CONTAINER_CLASSNAME = "circle-container";

const RadioIconContainer = ({
  children,
  size,
  vibe,
}: {
  children?: ReactNode;
  size?: "small" | "medium";
  vibe?: InputVibe;
}) => (
  <Box
    className={CIRCLE_CONTAINER_CLASSNAME}
    sx={(theme: Theme) => ({
      border: `1px solid ${theme.palette.border.neutral.intense}`,
      borderRadius: "50%",
      height: size === "small" ? 16 : 20,
      width: size === "small" ? 16 : 20,
      padding: size === "small" ? `calc(${theme.spacing(0.5)} - 1px)` : theme.spacing(0.5),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderColor: vibe === "form" ? theme.palette.border.form.intense : theme.palette.border.neutral.intense,
    })}
  >
    {children}
  </Box>
);

export const Radio = forwardRef<HTMLButtonElement, RadioProps>(
  (
    {
      alignContent,
      size = "small",
      disabled,
      vibe,
      title,
      value,
      label,
      errorMessage,
      checked,
      row,
      fullWidth,
      columns = 1,
      className,
      style,
      tooltipText,
    },
    ref
  ) => {
    const radioId = useId();

    const labelStyle: CSSProperties = useMemo(
      () => ({
        overflowWrap: "anywhere",
        marginTop: 0,
        width: row ? undefined : `${100 / columns}%`,
        ...style,
      }),
      [columns, row, style]
    );

    const labelWithTooltip = (
      <div style={{ display: "flex", alignItems: "center" }}>
        {label || value}
        {tooltipText && (
          <DashboardTooltip title={tooltipText} sx={{ marginLeft: 0.5 }} placement="bottom">
            <DashboardIcon size="small" sx={{ cursor: "pointer" }}>
              <Info />
            </DashboardIcon>
          </DashboardTooltip>
        )}
      </div>
    );

    return (
      <DashboardOptionLabel
        alignContent={alignContent}
        style={labelStyle}
        className={className}
        vibe={vibe}
        data-test="base-radio-group-choice"
        errorMessage={errorMessage}
        label={labelWithTooltip}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        inputId={radioId}
      >
        <MuiRadio
          id={radioId}
          ref={ref}
          color="primary"
          disableRipple
          size={size}
          value={value}
          checked={checked}
          disabled={disabled}
          sx={(theme) => ({
            height: size === "small" ? 16 : 20,
            width: size === "small" ? 16 : 20,
            padding: 0,
            "&:hover": {
              backgroundColor: "unset",
              [`& .${CIRCLE_CONTAINER_CLASSNAME}`]: {
                borderColor: theme.palette.border.brand.intense,
              },
            },
            "&.Mui-disabled": {
              opacity: 0.4,
            },
            "&:focus, &:focus-visible, &:focus-within": {
              outline: "none",
              [`& .${CIRCLE_CONTAINER_CLASSNAME}`]: {
                borderColor: theme.palette.border.brand.intense,
                outline: `2px solid ${theme.palette.border.brand.quiet}`,
              },
            },
          })}
          checkedIcon={
            <RadioIconContainer size={size} vibe={vibe}>
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.surface.brand.intense,
                  borderRadius: "50%",
                  height: "100%",
                  width: "100%",
                })}
              />
            </RadioIconContainer>
          }
          icon={<RadioIconContainer size={size} vibe={vibe} />}
          title={title}
        />
      </DashboardOptionLabel>
    );
  }
);

Radio.displayName = "Radio";
